<script setup lang="ts">
import { ref, watch, computed, onUnmounted } from 'vue'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import { useIncidentStore } from '@/stores/incident'
import { useSyncStore } from '@/stores/sync'
import { useAuthStore } from '@/stores/auth'
import { usePermissionsStore } from '@/stores/permissions'
import { useSubjectsStore } from '@/stores/subjects'
import { useFlyerVersionsStore } from '@/stores/flyerVersions'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import { useToastStore } from '@/stores/toast'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosButton from '@/components/ui/FosButton.vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'
import FlyerPreview from './FlyerPreview.vue'
import type { FlyerData } from '@/types'
import type { Subject } from '@/types'

const props = defineProps<{
  visible: boolean
  subject: Subject
  incidentId: string
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

const incidentStore = useIncidentStore()
const syncStore = useSyncStore()
const authStore = useAuthStore()
const permissionsStore = usePermissionsStore()
const subjectsStore = useSubjectsStore()
const flyerVersionsStore = useFlyerVersionsStore()
const { opRole } = useCurrentOpRole()
const toast = useToastStore()

const canGenerateFlyer = computed(() => permissionsStore.can('subjects.generate_flyer', opRole.value))

const generating = ref(false)
const agencyLogo = ref<string | null>(null)
const agencyName = ref<string | null>(null)
const agencyLogoOverride = ref<string | null>(null)
const secondaryLogoOverride = ref<string | null>(null)

const effectiveAgencyLogo = computed(() => agencyLogoOverride.value ?? agencyLogo.value)
const effectiveSecondaryLogo = computed(
  () => secondaryLogoOverride.value ?? incidentStore.currentIncident?.secondary_agency_logo ?? null,
)

const flyerData = ref<FlyerData>(buildFlyerData())

function buildFlyerData(): FlyerData {
  const s = props.subject
  const inc = incidentStore.currentIncident

  const missingSince = (() => {
    const d = s.date_reported ?? ''
    const t = s.time_reported ?? ''
    if (d && t) return `${d} at ${t}`
    return d || t || ''
  })()

  const situation = (() => {
    const lkp = s.lkp_description || ''
    const beh = s.behavior_if_lost || ''
    if (lkp && beh) return `${lkp} — ${beh}`
    return lkp || beh
  })()

  return {
    risk_category_label: s.risk_category ?? '',
    subject_name: `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim(),
    missing_since: missingSince,
    last_seen: s.lkp_description ?? '',
    age: s.age?.toString() ?? '',
    sex: s.gender ?? '',
    height: s.height ?? '',
    weight: s.weight ?? '',
    race: s.race ?? '',
    complexion: s.complexion ?? '',
    hair: s.hair_color ?? '',
    eyes: s.eye_color ?? '',
    clothing: s.clothing_description ?? '',
    situation,
    contact_phone: inc?.contact_phone ?? '',
  }
}

async function loadAgencyData() {
  const agencyId = authStore.userProfile?.agency_id
  if (!agencyId) return
  try {
    const snap = await getDoc(doc(db, 'agencies', agencyId))
    if (snap.exists()) {
      const data = snap.data()
      agencyLogo.value = (data.agency_logo as string | null) ?? null
      agencyName.value = (data.agency_name as string | null) ?? null
    }
  } catch {
    // Agency logo/name is optional — ignore fetch errors
  }
}

watch(
  () => props.visible,
  (v) => {
    if (!v) {
      flyerVersionsStore.teardown()
      agencyLogoOverride.value = null
      secondaryLogoOverride.value = null
      selectedVersionId.value = null
      return
    }
    // Prefer saved draft over rebuilding from subject fields
    flyerData.value = props.subject.flyer_draft
      ? { ...props.subject.flyer_draft }
      : buildFlyerData()
    loadAgencyData()
    flyerVersionsStore.subscribeToVersions(props.incidentId, props.subject.subject_id)
  },
)

// Auto-save draft on any field change (debounced 500ms)
let draftTimer: ReturnType<typeof setTimeout> | null = null
watch(
  flyerData,
  (val) => {
    if (!props.visible) return
    if (draftTimer) clearTimeout(draftTimer)
    draftTimer = setTimeout(() => {
      subjectsStore.saveFlyerDraft(props.incidentId, props.subject.subject_id, val)
    }, 500)
  },
  { deep: true },
)

function resetToSubjectData() {
  selectedVersionId.value = null
  flyerData.value = buildFlyerData()
  toast.add('Reset to subject data', 'default')
}

const subjectName = computed(
  () => `${props.subject.first_name ?? ''} ${props.subject.last_name ?? ''}`.trim(),
)

// Sort versions most-recent-first for display
const versionsDesc = computed(() =>
  [...flyerVersionsStore.versions].sort((a, b) => b.version_number - a.version_number),
)

// The permanent URL is stored on the subject after the first generation
const permanentUrl = computed(() => props.subject.permanent_flyer_url ?? null)

onUnmounted(() => {
  if (draftTimer) clearTimeout(draftTimer)
})

const selectedVersionId = ref<string | null>(null)

const selectedVersion = computed(
  () => flyerVersionsStore.versions.find((v) => v.version_id === selectedVersionId.value) ?? null,
)

function loadVersion(v: { version_id: string; flyer_data: FlyerData }) {
  selectedVersionId.value = v.version_id
  flyerData.value = { ...v.flyer_data }
}

function clearVersionSelection() {
  selectedVersionId.value = null
  flyerData.value = props.subject.flyer_draft
    ? { ...props.subject.flyer_draft }
    : buildFlyerData()
}

const copied = ref(false)
async function copyLink() {
  if (!permanentUrl.value) return
  await navigator.clipboard.writeText(permanentUrl.value)
  copied.value = true
  setTimeout(() => { copied.value = false }, 2000)
}

function formatVersionDate(ts: unknown): string {
  if (!ts) return ''
  // Firestore Timestamp has .toDate(), plain Date object otherwise
  const date = typeof (ts as { toDate?: () => Date }).toDate === 'function'
    ? (ts as { toDate: () => Date }).toDate()
    : new Date(ts as string)
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

async function generatePdf() {
  if (!syncStore.isOnline) {
    toast.add('Flyer generation requires a network connection', 'warning')
    return
  }

  generating.value = true
  try {
    const token = await authStore.currentUser?.getIdToken()
    if (!token) throw new Error('Not authenticated')

    // Calculate next version number before the API call so we can pass it in the request
    const nextVersionNumber = flyerVersionsStore.versions.length
      ? Math.max(...flyerVersionsStore.versions.map((v) => v.version_number)) + 1
      : 1

    const fd = flyerData.value
    const res = await fetch(
      `/api/incidents/${props.incidentId}/subjects/${props.subject.subject_id}/flyer`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          version_number: nextVersionNumber,
          risk_category_label: fd.risk_category_label || null,
          subject_name: fd.subject_name || null,
          missing_since: fd.missing_since || null,
          last_seen: fd.last_seen || null,
          age: fd.age || null,
          sex: fd.sex || null,
          height: fd.height || null,
          weight: fd.weight || null,
          race: fd.race || null,
          complexion: fd.complexion || null,
          hair: fd.hair || null,
          eyes: fd.eyes || null,
          clothing: fd.clothing || null,
          situation: fd.situation || null,
          contact_phone: fd.contact_phone || null,
          host_agency_logo: effectiveAgencyLogo.value || null,
          requesting_agency_logo: effectiveSecondaryLogo.value || null,
        }),
      },
    )

    const body = await res.json()
    if (res.status === 403) {
      toast.add("You don't have permission to perform this action.", 'error')
      return
    }
    if (!res.ok) {
      throw new Error(body?.error?.message ?? `Server error (${res.status})`)
    }

    const { version_url: versionUrl, permanent_url: newPermanentUrl } = body.data

    await flyerVersionsStore.createVersion(
      props.incidentId,
      props.subject.subject_id,
      { ...flyerData.value },
      versionUrl,
    )

    // On first generation, persist the permanent URL to the subject doc
    if (!props.subject.permanent_flyer_url) {
      await subjectsStore.savePermanentFlyerUrl(props.incidentId, props.subject.subject_id, newPermanentUrl)
    }

    toast.add('Flyer generated', 'success')
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Generation failed'
    toast.add(msg, 'error')
  } finally {
    generating.value = false
  }
}
</script>

<template>
  <FosDialog
    :open="visible"
    :title="`Generate Flyer — ${subjectName}`"
    max-width="max-w-5xl"
    body-class="overflow-hidden flex flex-col"
    @update:open="emit('update:visible', $event)"
  >
    <!-- Two-panel content area -->
    <div class="flex flex-1 min-h-0 -mx-6 border-t border-border" style="height: calc(90vh - 160px);">

      <!-- Left panel: version history -->
      <div class="w-56 shrink-0 flex flex-col border-r border-border">
        <div class="px-4 pt-3 pb-2 shrink-0 border-b border-border">
          <h3 class="text-[10px] font-label uppercase tracking-widest text-outline">Version History</h3>
        </div>

        <!-- Scrollable version list -->
        <div class="flex-1 overflow-y-auto">
          <!-- Loading -->
          <div v-if="flyerVersionsStore.loading" class="flex items-center justify-center py-6">
            <div class="w-4 h-4 border-2 border-outline/30 border-t-outline animate-spin" />
          </div>

          <!-- Empty state -->
          <div
            v-else-if="versionsDesc.length === 0"
            class="px-4 py-6 text-center"
          >
            <span class="material-symbols-outlined text-outline/40 text-3xl block mb-2">picture_as_pdf</span>
            <p class="text-xs text-outline">No versions yet.<br>Generate a PDF to save the first version.</p>
          </div>

          <!-- Version list -->
          <div v-else class="divide-y divide-border">
            <button
              v-for="v in versionsDesc"
              :key="v.version_id"
              type="button"
              class="w-full text-left px-4 py-3 transition-colors"
              :class="selectedVersionId === v.version_id
                ? 'bg-primary/10 border-l-2 border-primary'
                : 'hover:bg-surface-container-low border-l-2 border-transparent'"
              @click="loadVersion(v)"
            >
              <div class="flex items-center justify-between mb-0.5">
                <span class="text-xs font-mono font-semibold" :class="selectedVersionId === v.version_id ? 'text-primary' : 'text-on-surface'">v{{ v.version_number }}</span>
                <a
                  :href="v.pdf_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-[10px] font-label uppercase tracking-widest text-primary hover:text-primary/80 flex items-center gap-0.5"
                  @click.stop
                >
                  <span class="material-symbols-outlined text-[12px]">download</span>
                  PDF
                </a>
              </div>
              <div class="text-[11px] text-outline">{{ formatVersionDate(v.created_at) }}</div>
              <div v-if="v.created_by_name" class="text-[10px] text-outline-variant truncate">{{ v.created_by_name }}</div>
            </button>
          </div>
        </div>

      </div>

      <!-- Right panel: flyer preview -->
      <div class="flex-1 overflow-auto p-4 bg-surface-container-low">
        <!-- Version selection banner -->
        <div v-if="selectedVersion" class="flex items-center justify-between mb-3 px-3 py-2 bg-primary/8 border border-primary/20 rounded">
          <p class="text-xs text-primary flex items-center gap-1.5">
            <span class="material-symbols-outlined text-[14px]">history</span>
            Loaded from v{{ selectedVersion.version_number }} — edit below then generate a new version.
          </p>
          <button type="button" class="text-[10px] font-label uppercase tracking-widest text-primary hover:text-primary/70 flex items-center gap-0.5 shrink-0" @click="clearVersionSelection">
            <span class="material-symbols-outlined text-[12px]">close</span>
            Clear
          </button>
        </div>

        <div class="flex items-center justify-between mb-3">
          <p class="text-xs text-outline flex items-center gap-1.5">
            <span class="material-symbols-outlined text-[14px]">edit</span>
            Click any field to edit. Changes here do not affect the subject record.
          </p>
          <FosButton variant="tertiary" class="shrink-0" @click="resetToSubjectData">
            <span class="material-symbols-outlined text-[14px]">restart_alt</span>
            Reset to subject data
          </FosButton>
        </div>
        <FlyerPreview
          v-model="flyerData"
          :photo-url="subject.photo_url"
          :agency-logo="effectiveAgencyLogo"
          :agency-name="agencyName"
          :secondary-agency-logo="effectiveSecondaryLogo"
          @change:agency-logo="agencyLogoOverride = $event"
          @change:secondary-agency-logo="secondaryLogoOverride = $event"
        />
      </div>
    </div>

    <!-- Footer -->
    <div class="flex items-center justify-between mt-4 pt-4 border-t border-border shrink-0">
      <div class="flex items-center gap-3">
        <button
          v-if="permanentUrl"
          type="button"
          class="flex items-center gap-1.5 text-xs font-mono text-primary hover:text-primary/80 transition-colors cursor-pointer"
          @click="copyLink"
        >
          <span class="material-symbols-outlined text-[14px]">{{ copied ? 'check' : 'link' }}</span>
          {{ copied ? 'Copied!' : 'Copy link' }}
        </button>
        <div v-if="!syncStore.isOnline" class="flex items-center gap-1.5 text-amber-500 text-xs">
          <span class="material-symbols-outlined text-[14px]">wifi_off</span>
          Network required to generate.
        </div>
      </div>

      <PermissionGuard action="subjects.generate_flyer" :role="opRole">
        <FosButton
          variant="primary"
          :disabled="!syncStore.isOnline || generating"
          @click="generatePdf"
        >
          <span v-if="generating" class="w-4 h-4 border-2 border-outline/30 border-t-outline animate-spin" />
          <template v-else>
            <span class="material-symbols-outlined text-[16px]">picture_as_pdf</span>
            Generate New Version
          </template>
        </FosButton>
      </PermissionGuard>
    </div>
  </FosDialog>
</template>
